// ═══════════════════════════════════════════════════════════════════════
// Équipe IPM (mission SOGAS-RH-IPM-COMPLET-R1, 4 octobre 2026).
// Agents et Gestionnaires IPM, salariés SOGAS ou prestataires externes. La fonction IPM est
// nominative et datée : elle ne change jamais le poste RH.
//   • Créer un prestataire (sans fonction), changer un parrain, réinitialiser un mot de passe,
//     attribuer une fonction à un salarié : administration des accès (DRH, RH principal, administrateur).
//   • Attribuer la fonction d'un prestataire : administration des accès ou Gestionnaire IPM salarié,
//     jamais la personne qui a créé son accès (double regard, SPEC_TECHNIQUE § 7).
//   • Renouveler (y compris un accès expiré), suspendre, réactiver, terminer, enregistrer la
//     confidentialité : administration des accès ou Gestionnaire IPM salarié. « Terminé » est définitif.
//   • Un intervenant externe, même Gestionnaire, consulte seulement : aucun bouton d'écriture.
//   • Personne n'agit sur son propre accès. Rien ne se supprime. Le serveur contrôle chaque appel ;
//     son message d'erreur est affiché tel quel.
// Socle visuel SOGAS RH : icônes lucide uniquement, aucun emoji.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import {
  UserPlus, UserCog, Users, ShieldAlert, ChevronRight, RefreshCw, CirclePause, CirclePlay, CircleStop,
  ShieldCheck, KeyRound, Pencil, CalendarPlus, History, BriefcaseBusiness, RotateCcw, Check,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  EnTetePage, Bouton, Carte, Badge, Etat, BandeauInfo, EtatVide, Squelette, ChampRecherche, Champ, Grille,
  formaterDate, NON_RENSEIGNE,
} from '../design/composants';
import {
  useApi, Fenetre, FenetreSaisie, ChampForm, PiedFenetre, CaseACocher, ValeurACopier, messageErreur, nomSalarie,
  aujourdhuiIso, ajouterMois,
} from '../components/ipm/IpmCommun';

// ─── Libellés humains ───────────────────────────────────────────────────────
const LIBELLE_FONCTION = { AGENT: 'Agent IPM', GESTIONNAIRE: 'Gestionnaire IPM' };
const LIBELLE_TYPE = { SALARIE: 'Salarié SOGAS', EXTERNE: 'Prestataire externe' };
const STATUT_EFFECTIF = {
  ACTIVE: { libelle: 'Actif', ton: 'succes' },
  A_VENIR: { libelle: 'À venir', ton: 'info' },
  SUSPENDUE: { libelle: 'Suspendu', ton: 'alerte' },
  EXPIREE: { libelle: 'Expiré', ton: 'alerte' },
  SANS_FONCTION: { libelle: 'En attente d\'une fonction', ton: 'info' },
  TERMINEE: { libelle: 'Terminé', ton: 'neutre' },
  BLOQUEE: { libelle: 'Bloqué — confidentialité manquante', ton: 'danger' },
};
const STATUT_ACCES = { ACTIF: 'Actif', SUSPENDU: 'Suspendu', TERMINE: 'Terminé' };
const STATUT_FONCTION = { ACTIVE: { libelle: 'Active', ton: 'succes' }, SUSPENDUE: { libelle: 'Suspendue', ton: 'alerte' }, TERMINEE: { libelle: 'Terminée', ton: 'neutre' } };
const ALERTE_COURTE = { CONFIDENTIALITE_MANQUANTE: 'Confidentialité à enregistrer', PARRAIN_INACTIF: 'Parrain inactif', ECHEANCE_PROCHE: 'Échéance proche' };
const EVENEMENTS = {
  CREATION: 'Accès créé',
  ATTRIBUTION: 'Fonction attribuée',
  RENOUVELLEMENT: 'Accès renouvelé',
  SUSPENSION: 'Suspension',
  REACTIVATION: 'Réactivation',
  FIN: 'Accès terminé',
  EXPIRATION: 'Expiré automatiquement',
  PROLONGATION: 'Fonction prolongée',
  CONFIDENTIALITE: 'Engagement de confidentialité enregistré',
  PARRAIN: 'Parrain modifié',
  MOT_DE_PASSE: 'Mot de passe réinitialisé',
  MODIFICATION: 'Coordonnées modifiées',
  ALERTE_J30: 'Alerte d\'échéance envoyée (30 jours)',
  ALERTE_J7: 'Alerte d\'échéance envoyée (7 jours)',
  PARRAIN_INACTIF: 'Parrain devenu inactif signalé',
  REPRISE: 'Reprise de l\'ancienne habilitation de poste',
};
const MSG_SUR_SOI = 'Vous ne pouvez pas modifier votre propre accès IPM.';
const MSG_DOUBLE_REGARD = 'La fonction IPM d\'un prestataire est attribuée par une autre personne que celle qui a créé son accès.';
const MSG_LECTURE_EXTERNE = 'Consultation seulement : un intervenant externe ne modifie pas les accès IPM.';
const AIDE_SECOURS = 'Désigné à l\'avance pour valider lorsqu\'aucun autre Gestionnaire IPM n\'est disponible ; rarement sollicité.';

const nomPersonne = (p) => nomSalarie(p);
const libelleFonction = (r) => (r.fonction ? LIBELLE_FONCTION[r.fonction] || r.fonction : 'Aucune fonction');
function mentionsFonction(r) {
  if (!r.fonction) return [];
  const m = [];
  if (r.valideur_secours) m.push('valideur de secours');
  if (r.acces_medical === false) m.push('sans données médicales');
  return m;
}
/** Statut affiché : celui du serveur (statut_effectif), précisé pour un prestataire par l'état de son ACCÈS :
 *    • accès terminé → « Terminé » (définitif) ; suspendu → « Suspendu » ;
 *    • accès encore ACTIF dont la date de fin est passée → « Expiré » (il se renouvelle), même si sa
 *      fonction a été close à l'échéance ;
 *    • accès en cours sans fonction en cours → « En attente d'une fonction » (connexion refusée). */
function statutDe(r) {
  if (r.type !== 'EXTERNE') return r.statut_effectif;
  if (r.acces_statut === 'TERMINE') return 'TERMINEE';
  if (r.acces_statut === 'SUSPENDU') return 'SUSPENDUE';
  if (r.acces_statut === 'ACTIF' && r.acces_fin && r.acces_fin < aujourdhuiIso()) return 'EXPIREE';
  if (r.statut_effectif === 'TERMINEE' || (r.statut_effectif === 'ACTIVE' && !r.fonction)) return 'SANS_FONCTION';
  return r.statut_effectif;
}
const memeId = (a, b) => !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();
function lendemain(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
const debutDe = (r) => (r.type === 'EXTERNE' ? r.acces_debut : r.fonction_debut);
const finDe = (r) => (r.type === 'EXTERNE' ? r.acces_fin : r.fonction_fin);
function echeance(r) {
  if (statutDe(r) === 'TERMINEE') return '—';
  const j = r.jours_restants;
  if (j === null || j === undefined) return 'Sans échéance';
  if (j < 0) return 'Échue';
  if (j === 0) return 'Aujourd\'hui';
  return `${j} jour${j > 1 ? 's' : ''}`;
}
const plusTardif = (a, b) => (a > b ? a : b);

// ─── Recherche d'un salarié pouvant recevoir une fonction ou être parrain ───
function RechercheSalarieEquipe({ api, valeur, onChoix, moi, libelle = 'Rechercher un salarié', exclureMoi = false }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const [erreur, setErreur] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return undefined; }
    const t = setTimeout(() => {
      setErreur(null);
      api.get('/equipe/salaries', { search: q }).then(setRes).catch((e) => { setRes([]); setErreur(messageErreur(e)); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, api]);
  if (valeur) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Badge ton="info">{nomSalarie(valeur)} · {valeur.matricule}</Badge>
        {valeur.poste && <span className="rh-cellule-secondaire">{valeur.poste}</span>}
        <Bouton petit variante="discret" Icone={RotateCcw} onClick={() => onChoix(null)}>Changer</Bouton>
      </div>
    );
  }
  return (
    <div>
      <ChampRecherche valeur={q} onChange={setQ} placeholder="Nom, prénom ou matricule" libelle={libelle} />
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {res.length > 0 && (
        <ul className="rh-liste" style={{ marginTop: 6, maxHeight: 220, overflowY: 'auto' }}>
          {res.map((s) => {
            const soi = exclureMoi && memeId(s.id, moi);
            return (
              <li key={s.id}>
                <span style={{ flex: 1, minWidth: 0 }}>{nomSalarie(s)} <span className="rh-cellule-secondaire">· {s.matricule}{s.poste ? ` · ${s.poste}` : ''}{s.site_nom ? ` · ${s.site_nom}` : ''}</span></span>
                {soi ? <span className="rh-cellule-secondaire">Vous-même</span>
                  : <Bouton petit onClick={() => { onChoix(s); setQ(''); }}>Choisir</Bouton>}
              </li>
            );
          })}
        </ul>
      )}
      {q.trim().length >= 2 && res.length === 0 && !erreur && <p className="rh-cellule-secondaire" style={{ marginTop: 6 }}>Aucun salarié actif ne correspond.</p>}
    </div>
  );
}

// ─── Identifiant et mot de passe provisoire : affichés une seule fois ───────
function SecretUnique({ identifiant, motDePasse, onTermine, texteBouton = 'J\'ai noté ces informations' }) {
  return (
    <>
      <BandeauInfo ton="alerte" titre="Affiché une seule fois">
        Ce mot de passe provisoire ne sera plus jamais affiché. Notez-le et remettez-le à la personne par un moyen sûr ; elle devra le changer à sa première connexion.
      </BandeauInfo>
      <ValeurACopier libelle="Identifiant de connexion" valeur={identifiant} />
      <ValeurACopier libelle="Mot de passe provisoire" valeur={motDePasse} />
      <PiedFenetre><Bouton variante="primaire" Icone={Check} onClick={onTermine}>{texteBouton}</Bouton></PiedFenetre>
    </>
  );
}

// ─── Ajouter un prestataire externe ─────────────────────────────────────────
const VIDE_PRESTATAIRE = {
  prenom: '', nom: '', email: '', telephone: '', organisme: '', mission: '', piece_reference: '',
  date_debut: aujourdhuiIso(), date_fin: '', conf_date: '', conf_reference: '', motif: '',
};
function FenetrePrestataire({ api, dureeMax, onFermer, onFait }) {
  const [f, setF] = useState(VIDE_PRESTATAIRE);
  const [parrain, setParrain] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [cree, setCree] = useState(null);
  const maj = (cle) => (e) => setF({ ...f, [cle]: e.target.value });
  const finMax = ajouterMois(f.date_debut, dureeMax);
  const confSaisie = !!(f.conf_date || f.conf_reference.trim());
  const pret = f.prenom.trim() && f.nom.trim() && f.email.trim() && f.mission.trim() && f.piece_reference.trim() && parrain && f.date_debut && f.date_fin;
  const enregistrer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      const r = await api.post('/equipe/intervenants', {
        prenom: f.prenom, nom: f.nom, email: f.email, telephone: f.telephone || undefined, organisme: f.organisme || undefined,
        mission: f.mission, piece_reference: f.piece_reference, parrain_id: parrain.id, date_debut: f.date_debut, date_fin: f.date_fin,
        confidentialite: confSaisie ? { date: f.conf_date, reference: f.conf_reference } : undefined,
        motif: f.motif || undefined,
      });
      setCree(r);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  if (cree) {
    return (
      <Fenetre titre="Prestataire créé" onFermer={onFait}>
        <p style={{ fontSize: 13, marginTop: 0 }}>
          L'accès de {f.prenom} {f.nom.toUpperCase()} est créé, sans fonction IPM pour l'instant.
        </p>
        <BandeauInfo ton="info">{MSG_DOUBLE_REGARD}</BandeauInfo>
        <SecretUnique identifiant={cree.identifiant} motDePasse={cree.mot_de_passe_provisoire} onTermine={onFait} />
      </Fenetre>
    );
  }
  const grille = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' };
  return (
    <Fenetre titre="Ajouter un prestataire externe" onFermer={onFermer} large>
      <BandeauInfo ton="info">Un prestataire n'est pas un salarié : il n'apparaît ni dans les effectifs ni dans la paie et n'accède qu'à l'IPM. Son accès est daté ({dureeMax} mois au plus) et suivi par un parrain salarié de SOGAS.</BandeauInfo>
      <h3 style={{ fontSize: 14, margin: '12px 0 8px' }}>Identité et coordonnées</h3>
      <div style={grille}>
        <ChampForm libelle="Prénom" requis id="x-prenom"><input id="x-prenom" className="rh-champ-saisie" value={f.prenom} onChange={maj('prenom')} /></ChampForm>
        <ChampForm libelle="Nom" requis id="x-nom"><input id="x-nom" className="rh-champ-saisie" value={f.nom} onChange={maj('nom')} /></ChampForm>
        <ChampForm libelle="Adresse e-mail" requis id="x-email"><input id="x-email" className="rh-champ-saisie" type="email" value={f.email} onChange={maj('email')} /></ChampForm>
        <ChampForm libelle="Téléphone" id="x-tel"><input id="x-tel" className="rh-champ-saisie" type="tel" value={f.telephone} onChange={maj('telephone')} /></ChampForm>
        <ChampForm libelle="Organisme" id="x-org" aide="Cabinet, société ou organisme d'origine."><input id="x-org" className="rh-champ-saisie" value={f.organisme} onChange={maj('organisme')} /></ChampForm>
        <ChampForm libelle="Référence de la pièce d'identité" requis id="x-piece" aide="Numéro de la carte d'identité ou du passeport."><input id="x-piece" className="rh-champ-saisie" value={f.piece_reference} onChange={maj('piece_reference')} /></ChampForm>
      </div>
      <ChampForm libelle="Mission" requis id="x-mission" aide="Objet de l'intervention pour l'IPM."><textarea id="x-mission" className="rh-champ-saisie" rows={2} value={f.mission} onChange={maj('mission')} /></ChampForm>
      <ChampForm libelle="Parrain (salarié SOGAS actif)" requis aide="Le parrain répond de la présence du prestataire et reçoit les alertes d'échéance.">
        <RechercheSalarieEquipe api={api} valeur={parrain} onChoix={setParrain} libelle="Rechercher le parrain" />
      </ChampForm>

      <h3 style={{ fontSize: 14, margin: '12px 0 8px' }}>Période d'accès</h3>
      <div style={grille}>
        <ChampForm libelle="Date de début" requis id="x-debut"><input id="x-debut" className="rh-champ-saisie" type="date" min={aujourdhuiIso()} value={f.date_debut} onChange={maj('date_debut')} /></ChampForm>
        <ChampForm libelle="Date de fin" requis id="x-fin" aide={`${dureeMax} mois au plus${finMax ? ` : au plus tard le ${formaterDate(finMax)}` : ''}. Sans renouvellement explicite, l'accès s'arrête ce jour-là.`}>
          <input id="x-fin" className="rh-champ-saisie" type="date" min={f.date_debut} max={finMax || undefined} value={f.date_fin} onChange={maj('date_fin')} />
        </ChampForm>
      </div>

      <h3 style={{ fontSize: 14, margin: '12px 0 8px' }}>Engagement de confidentialité</h3>
      <p className="rh-form-aide" style={{ marginTop: 0 }}>Facultatif à la création, mais obligatoire avant toute fonction IPM : sans lui, le compte reste bloqué.</p>
      <div style={grille}>
        <ChampForm libelle="Date de signature" id="x-cdate"><input id="x-cdate" className="rh-champ-saisie" type="date" max={aujourdhuiIso()} value={f.conf_date} onChange={maj('conf_date')} /></ChampForm>
        <ChampForm libelle="Référence du document signé" id="x-cref"><input id="x-cref" className="rh-champ-saisie" value={f.conf_reference} onChange={maj('conf_reference')} /></ChampForm>
      </div>

      <h3 style={{ fontSize: 14, margin: '12px 0 8px' }}>Fonction IPM</h3>
      <BandeauInfo ton="info">{MSG_DOUBLE_REGARD} L'accès est créé sans fonction : tant qu'aucune fonction n'est attribuée, la connexion est refusée.</BandeauInfo>
      <ChampForm libelle="Motif" id="x-motif" aide="Contexte de la création (facultatif)."><textarea id="x-motif" className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></ChampForm>

      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <PiedFenetre>
        <Bouton variante="discret" onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={UserPlus} disabled={!pret || envoi} onClick={enregistrer}>{envoi ? 'Création…' : 'Créer l\'accès'}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

// ─── Attribuer une fonction (salarié recherché, ou personne déjà connue) ─────
function FenetreFonction({ api, moi, personne = null, onFermer, onFait }) {
  const [salarie, setSalarie] = useState(null);
  const [f, setF] = useState({ fonction: 'AGENT', acces_medical: false, valideur_secours: false, date_debut: aujourdhuiIso(), date_fin: '', motif: '' });
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const cible = personne || salarie;
  const soi = !!cible && memeId(cible.id, moi);
  const externe = personne && personne.type === 'EXTERNE';
  const maj = (cle) => (e) => setF({ ...f, [cle]: e.target.value });
  const enregistrer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      const r = await api.post('/equipe/fonctions', {
        personne_id: cible.id, fonction: f.fonction, acces_medical: f.acces_medical, valideur_secours: f.fonction === 'GESTIONNAIRE' && f.valideur_secours,
        date_debut: f.date_debut || undefined, date_fin: f.date_fin || undefined, motif: f.motif,
      });
      onFait(r);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre={personne ? `Attribuer une fonction — ${personne.nom}` : 'Attribuer une fonction à un salarié'} onFermer={onFermer}>
      <BandeauInfo ton="info">{externe
        ? 'La fonction ne peut pas dépasser la fin de l\'accès du prestataire. L\'accès aux données médicales n\'est accordé que si la case est cochée.'
        : 'Le poste RH du salarié ne change pas : la fonction IPM est nominative, datée et s\'ajoute à son poste.'}</BandeauInfo>
      {!personne && (
        <ChampForm libelle="Salarié" requis>
          <RechercheSalarieEquipe api={api} valeur={salarie} onChoix={setSalarie} moi={moi} exclureMoi />
        </ChampForm>
      )}
      {soi && <BandeauInfo ton="alerte">{MSG_SUR_SOI}</BandeauInfo>}
      <ChampForm libelle="Fonction" requis id="fn-fonction">
        <select id="fn-fonction" className="rh-champ-saisie" value={f.fonction} onChange={maj('fonction')}>
          <option value="AGENT">Agent IPM</option>
          <option value="GESTIONNAIRE">Gestionnaire IPM</option>
        </select>
      </ChampForm>
      <p className="rh-form-aide" style={{ marginTop: -6 }}>
        {f.fonction === 'AGENT' ? 'Saisit les prestations, propose les corrections et les retenues ; ne valide pas.' : 'Fait tout ce que fait l\'Agent et assure la supervision : validations, confirmation de l\'arrêt des périodes, journal de contrôle, suivi des accès.'}
      </p>
      <CaseACocher coche={f.acces_medical} onChange={(x) => setF({ ...f, acces_medical: x })} libelle="Accès aux données médicales"
        aide="Soins, prestataires de santé et ayants droit concernés." />
      {f.fonction === 'GESTIONNAIRE' && (
        <CaseACocher coche={f.valideur_secours} onChange={(x) => setF({ ...f, valideur_secours: x })} libelle="Valideur de secours" aide={AIDE_SECOURS} />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 16px' }}>
        <ChampForm libelle="Date de début" requis id="fn-debut"><input id="fn-debut" className="rh-champ-saisie" type="date" min={aujourdhuiIso()} value={f.date_debut} onChange={maj('date_debut')} /></ChampForm>
        <ChampForm libelle="Date de fin" id="fn-fin" aide={externe && personne.acces_fin ? `Facultative ; au plus tard le ${formaterDate(personne.acces_fin)}.` : 'Facultative.'}>
          <input id="fn-fin" className="rh-champ-saisie" type="date" min={f.date_debut} max={externe ? personne.acces_fin || undefined : undefined} value={f.date_fin} onChange={maj('date_fin')} />
        </ChampForm>
      </div>
      <ChampForm libelle="Motif" requis id="fn-motif" aide="5 caractères au moins. Conservé dans l'historique."><textarea id="fn-motif" className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></ChampForm>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <PiedFenetre>
        <Bouton variante="discret" onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Check} disabled={!cible || soi || envoi || f.motif.trim().length < 5} onClick={enregistrer}>{envoi ? 'Enregistrement…' : 'Attribuer'}</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

// ─── Détail d'une personne : identité, fonctions, historique, actions ────────
function DetailPersonne({ api, ligne, droits, moi, dureeMax, onFermer, onChange }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [action, setAction] = useState(null);
  const [secret, setSecret] = useState(null);
  const [message, setMessage] = useState(null);
  const charger = useCallback(() => {
    api.get(`/equipe/personnes/${ligne.personne_id}`).then(setD).catch((e) => setErreur(messageErreur(e)));
  }, [api, ligne.personne_id]);
  useEffect(() => { charger(); }, [charger]);
  const fait = (r) => { setAction(null); setMessage(r && r.message ? r.message : null); charger(); onChange(); };

  // Droits renvoyés par le serveur (GET /equipe) : un intervenant externe n'a ni l'un ni l'autre,
  // il consulte seulement. Le serveur refuse de toute façon (403) ce qui n'est pas permis.
  const administration = !!droits.administration;
  const suivi = administration || !!droits.supervision;
  const lectureSeule = !suivi;
  const soi = memeId(ligne.personne_id, moi);
  const p = d?.personne;
  const x = d?.externe;
  const enCours = d ? d.fonctions.find((f) => f.statut !== 'TERMINEE') : null;
  const statutLigne = statutDe(ligne);
  const statutEffectif = STATUT_EFFECTIF[statutLigne] || { libelle: statutLigne, ton: 'neutre' };
  const actif = x ? x.statut !== 'TERMINE' : true;
  const expire = !!x && x.statut === 'ACTIF' && !!x.date_fin && x.date_fin < aujourdhuiIso();
  const createur = !!x && memeId(x.cree_par_id, moi);
  // Attribution : salarié → administration ; prestataire → administration ou Gestionnaire IPM salarié,
  // jamais la personne qui a créé son accès, et seulement sur un accès actif et en cours.
  const peutAttribuer = !soi && !enCours && actif && (x
    ? suivi && !createur && x.statut === 'ACTIF' && !expire
    : administration);
  // À la place du bouton, pour un prestataire sans fonction : la règle du double regard.
  const phraseDoubleRegard = !!x && !soi && !enCours && actif && !peutAttribuer && (createur || !suivi);
  const nom = p ? nomPersonne(p) : nomPersonne(ligne);
  const motif = { cle: 'motif', libelle: 'Motif', requis: true, texteLong: true, aide: '5 caractères au moins. Conservé dans l\'historique.' };
  const finRenouvMax = x ? ajouterMois(plusTardif(aujourdhuiIso(), x.date_fin), dureeMax) : null;
  // Nouvelle fin : après l'ancienne (même échue) et après aujourd'hui.
  const finRenouvMin = x ? plusTardif(lendemain(aujourdhuiIso()) || '', lendemain(x.date_fin) || '') : null;

  return (
    <Fenetre titre={nom || 'Détail'} onFermer={onFermer} large>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!d && !erreur && <Squelette hauteur={160} />}
      {d && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <Badge ton={p.type === 'EXTERNE' ? 'info' : 'neutre'}>{LIBELLE_TYPE[p.type]}</Badge>
            <Etat ton={statutEffectif.ton}>{statutEffectif.libelle}</Etat>
            {(ligne.alertes || []).map((a) => <Badge key={a.code} ton="alerte">{a.message}</Badge>)}
          </div>
          {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
          {soi && <BandeauInfo ton="alerte">{MSG_SUR_SOI}</BandeauInfo>}
          {lectureSeule && !soi && <BandeauInfo ton="info">{MSG_LECTURE_EXTERNE}</BandeauInfo>}
          {expire && (
            <BandeauInfo ton="alerte" titre="Accès expiré">
              Cet accès a expiré le {formaterDate(x.date_fin)} : la connexion est refusée. Il peut être renouvelé ; seule la fin décidée (« Terminer l'accès ») est définitive.
            </BandeauInfo>
          )}
          {x && !actif && <BandeauInfo ton="neutre">Accès terminé, définitivement : il ne se renouvelle pas. Pour une nouvelle mission, un nouvel accès doit être créé.</BandeauInfo>}

          <Carte titre="Identité" Icone={Users}>
            <Grille>
              <Champ libelle="Identifiant" valeur={x ? x.identifiant : p.matricule} />
              <Champ libelle="Type" valeur={LIBELLE_TYPE[p.type]} />
              {p.type === 'SALARIE' && <Champ libelle="Poste RH" valeur={p.poste} />}
              {x && <Champ libelle="Organisme" valeur={x.organisme} />}
              {x && <Champ libelle="Adresse e-mail" valeur={x.email} />}
              {x && <Champ libelle="Téléphone" valeur={x.telephone} />}
              {x && <Champ libelle="Pièce d'identité" valeur={x.piece_reference} />}
              {x && <Champ libelle="Parrain" enfant={x.parrain ? (
                <span>{x.parrain.nom} <span className="rh-cellule-secondaire">· {x.parrain.matricule}</span>
                  {!x.parrain.actif && <div><Badge ton="alerte">N'est plus un salarié actif : désigner un nouveau parrain</Badge></div>}</span>
              ) : null} />}
              {x && <Champ libelle="Période d'accès" valeur={`Du ${formaterDate(x.date_debut)} au ${formaterDate(x.date_fin)}`} />}
              {x && <Champ libelle="Statut de l'accès" valeur={STATUT_ACCES[x.statut] || x.statut} />}
              {x && <Champ libelle="Engagement de confidentialité" enfant={x.confidentialite_le
                ? <span>Signé le {formaterDate(x.confidentialite_le)}{x.confidentialite_reference ? <span className="rh-cellule-secondaire"> · réf. {x.confidentialite_reference}</span> : null}</span>
                : <Badge ton="danger">Non enregistré</Badge>} />}
              {x && x.remunere_par_ipm && <Champ libelle="Rémunération" valeur="Rémunéré par l'IPM (hors paie SOGAS)" />}
              {x && <Champ libelle="Compte" valeur={x.compte_bloque ? 'Bloqué après des échecs de connexion' : x.premier_connexion ? 'Mot de passe provisoire à changer' : 'Ouvert'} />}
              {x && <Champ libelle="Dernière connexion" valeur={x.derniere_connexion ? new Date(x.derniere_connexion).toLocaleString('fr-FR') : 'Jamais'} />}
              {x && <Champ libelle="Créé" valeur={`${formaterDate(x.created_at)}${x.cree_par ? ` par ${x.cree_par}` : ''}`} />}
            </Grille>
            {x && <div style={{ marginTop: 10 }}><Champ libelle="Mission" valeur={x.mission} /></div>}
          </Carte>

          {x && !soi && actif && suivi && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 16px' }}>
              <Bouton petit Icone={RefreshCw} onClick={() => setAction({ type: 'renouveler' })}>Renouveler</Bouton>
              {x.statut === 'ACTIF' && <Bouton petit Icone={CirclePause} onClick={() => setAction({ type: 'suspendre' })}>Suspendre</Bouton>}
              {x.statut === 'SUSPENDU' && <Bouton petit Icone={CirclePlay} onClick={() => setAction({ type: 'reactiver' })}>Réactiver</Bouton>}
              <Bouton petit Icone={ShieldCheck} onClick={() => setAction({ type: 'confidentialite' })}>Enregistrer la confidentialité</Bouton>
              {administration && <Bouton petit Icone={UserCog} onClick={() => setAction({ type: 'parrain' })}>Changer de parrain</Bouton>}
              {administration && <Bouton petit Icone={Pencil} onClick={() => setAction({ type: 'coordonnees' })}>Modifier les coordonnées</Bouton>}
              {administration && <Bouton petit Icone={KeyRound} onClick={() => setAction({ type: 'mot-de-passe' })}>Réinitialiser le mot de passe</Bouton>}
              <Bouton petit variante="danger" Icone={CircleStop} onClick={() => setAction({ type: 'terminer' })}>Terminer l'accès</Bouton>
            </div>
          )}

          <Carte titre="Fonctions IPM" Icone={BriefcaseBusiness}
            action={peutAttribuer && (
              <Bouton petit Icone={UserCog} onClick={() => setAction({ type: 'attribuer' })}>Attribuer une fonction</Bouton>
            )}>
            {phraseDoubleRegard && <BandeauInfo ton="info">{MSG_DOUBLE_REGARD}</BandeauInfo>}
            {x && expire && !enCours && !soi && suivi && <p className="rh-cellule-secondaire" style={{ marginTop: 0 }}>Renouvelez d'abord l'accès pour pouvoir lui attribuer une fonction.</p>}
            {d.fonctions.length === 0 ? <EtatVide titre="Aucune fonction IPM" texte={x && !x.confidentialite_le ? 'L\'engagement de confidentialité doit être enregistré avant toute fonction.' : null} /> : (
              <ul className="rh-liste">
                {d.fonctions.map((f) => {
                  const st = STATUT_FONCTION[f.statut] || { libelle: f.statut, ton: 'neutre' };
                  const m = mentionsFonction(f);
                  return (
                    <li key={f.id} style={{ alignItems: 'flex-start' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <strong>{LIBELLE_FONCTION[f.fonction] || f.fonction}</strong>{m.length ? <span className="rh-cellule-secondaire"> · {m.join(' · ')}</span> : null}
                        <div className="rh-cellule-secondaire">
                          Du {formaterDate(f.date_debut)}{f.date_fin ? ` au ${formaterDate(f.date_fin)}` : ', sans date de fin'}
                          {f.origine === 'REPRISE' ? ' · reprise de l\'ancienne habilitation de poste' : f.attribue_par_nom ? ` · attribuée par ${f.attribue_par_nom}` : ''}
                        </div>
                        {f.motif && <div className="rh-cellule-secondaire">Motif : {f.motif}</div>}
                        {f.motif_fin && <div className="rh-cellule-secondaire">Fin : {f.motif_fin}{f.termine_le ? ` (${formaterDate(f.termine_le)})` : ''}</div>}
                        {!soi && !lectureSeule && f.statut !== 'TERMINEE' && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                            {suivi && f.statut === 'ACTIVE' && <Bouton petit Icone={CirclePause} onClick={() => setAction({ type: 'f-suspendre', f })}>Suspendre</Bouton>}
                            {administration && f.statut === 'SUSPENDUE' && <Bouton petit Icone={CirclePlay} onClick={() => setAction({ type: 'f-reactiver', f })}>Réactiver</Bouton>}
                            {administration && <Bouton petit Icone={CalendarPlus} onClick={() => setAction({ type: 'f-prolonger', f })}>Prolonger</Bouton>}
                            {suivi && <Bouton petit variante="danger" Icone={CircleStop} onClick={() => setAction({ type: 'f-terminer', f })}>Terminer</Bouton>}
                          </div>
                        )}
                      </span>
                      <Etat ton={st.ton}>{st.libelle}</Etat>
                    </li>
                  );
                })}
              </ul>
            )}
          </Carte>

          <Carte titre="Historique" Icone={History}>
            {d.journal.length === 0 ? <EtatVide titre="Aucun événement" /> : (
              <ul className="rh-liste">
                {d.journal.map((j) => (
                  <li key={j.id} style={{ alignItems: 'flex-start' }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <strong>{EVENEMENTS[j.evenement] || 'Événement'}</strong>
                      {j.details && j.details.nouvelle_fin ? <span className="rh-cellule-secondaire"> · fin portée au {formaterDate(j.details.nouvelle_fin)}</span> : null}
                      {j.motif && <div className="rh-cellule-secondaire">Motif : {j.motif}</div>}
                      <div className="rh-cellule-secondaire">{new Date(j.created_at).toLocaleString('fr-FR')} · {j.auteur || NON_RENSEIGNE}</div>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Carte>
        </>
      )}

      {/* ─── Actions sur l'accès d'un prestataire ─── */}
      {action?.type === 'renouveler' && x && (
        <FenetreSaisie titre={`Renouveler l'accès — ${nom}`} onFermer={() => setAction(null)} onFait={fait}
          avertissement={`Fin actuelle : ${formaterDate(x.date_fin)}${expire ? ' (accès expiré)' : ''}. Le renouvellement est explicite et ne peut pas dépasser ${dureeMax} mois.`}
          champs={[
            { cle: 'date_fin', libelle: 'Nouvelle date de fin', requis: true, type: 'date', min: finRenouvMin || undefined, max: finRenouvMax || undefined, aide: finRenouvMax ? `Au plus tard le ${formaterDate(finRenouvMax)}.` : null },
            motif,
          ]}
          libelleBouton="Renouveler" valider={(v) => api.post(`/equipe/intervenants/${ligne.personne_id}/renouveler`, v)} />
      )}
      {action?.type === 'suspendre' && (
        <FenetreSaisie titre={`Suspendre l'accès — ${nom}`} onFermer={() => setAction(null)} onFait={fait}
          avertissement="La personne ne peut plus se connecter tant que l'accès est suspendu. Rien n'est supprimé." champs={[motif]}
          libelleBouton="Suspendre" valider={(v) => api.post(`/equipe/intervenants/${ligne.personne_id}/suspendre`, v)} />
      )}
      {action?.type === 'reactiver' && (
        <FenetreSaisie titre={`Réactiver l'accès — ${nom}`} onFermer={() => setAction(null)} onFait={fait} champs={[motif]}
          libelleBouton="Réactiver" valider={(v) => api.post(`/equipe/intervenants/${ligne.personne_id}/reactiver`, v)} />
      )}
      {action?.type === 'terminer' && (
        <FenetreSaisie titre={`Terminer l'accès — ${nom}`} onFermer={() => setAction(null)} onFait={fait} tonAvertissement="alerte"
          avertissement="Définitif : l'accès et sa fonction IPM sont terminés. L'historique est conservé ; pour une nouvelle mission, un nouvel accès devra être créé."
          champs={[motif]} libelleBouton="Terminer l'accès" valider={(v) => api.post(`/equipe/intervenants/${ligne.personne_id}/terminer`, v)} />
      )}
      {action?.type === 'confidentialite' && (
        <FenetreSaisie titre={`Engagement de confidentialité — ${nom}`} onFermer={() => setAction(null)} onFait={fait}
          avertissement="Obligatoire avant toute fonction IPM : sans lui, le compte reste bloqué."
          initial={{ date: x?.confidentialite_le || '', reference: x?.confidentialite_reference || '' }}
          champs={[
            { cle: 'date', libelle: 'Date de signature', requis: true, type: 'date', max: aujourdhuiIso() },
            { cle: 'reference', libelle: 'Référence du document signé', requis: true },
          ]}
          valider={(v) => api.post(`/equipe/intervenants/${ligne.personne_id}/confidentialite`, v)} />
      )}
      {action?.type === 'parrain' && (
        <FenetreSaisie titre={`Changer de parrain — ${nom}`} onFermer={() => setAction(null)} onFait={fait}
          champs={[motif]} desactive={(v) => !v.parrain}
          enfant={(v, setV) => (
            <ChampForm libelle="Nouveau parrain (salarié SOGAS actif)" requis>
              <RechercheSalarieEquipe api={api} valeur={v.parrain || null} onChoix={(s) => setV({ ...v, parrain: s })} libelle="Rechercher le nouveau parrain" />
            </ChampForm>
          )}
          valider={(v) => api.post(`/equipe/intervenants/${ligne.personne_id}/parrain`, { parrain_id: v.parrain && v.parrain.id, motif: v.motif })} />
      )}
      {action?.type === 'coordonnees' && x && (
        <FenetreSaisie titre={`Modifier les coordonnées — ${nom}`} onFermer={() => setAction(null)} onFait={fait}
          initial={{ email: x.email || '', telephone: x.telephone || '', organisme: x.organisme || '', mission: x.mission || '' }}
          champs={[
            { cle: 'email', libelle: 'Adresse e-mail', requis: true, type: 'email' },
            { cle: 'telephone', libelle: 'Téléphone', type: 'tel' },
            { cle: 'organisme', libelle: 'Organisme' },
            { cle: 'mission', libelle: 'Mission', requis: true, texteLong: true },
          ]}
          valider={(v) => api.patch(`/equipe/intervenants/${ligne.personne_id}`, v)} />
      )}
      {action?.type === 'mot-de-passe' && (
        <FenetreSaisie titre={`Réinitialiser le mot de passe — ${nom}`} onFermer={() => setAction(null)} champs={[]} tonAvertissement="alerte"
          avertissement="Un nouveau mot de passe provisoire est généré et affiché une seule fois. L'ancien ne fonctionne plus ; le compte est débloqué et le mot de passe devra être changé à la prochaine connexion."
          libelleBouton="Réinitialiser"
          onFait={(r) => { setAction(null); setSecret(r); charger(); onChange(); }}
          valider={() => api.post(`/equipe/intervenants/${ligne.personne_id}/mot-de-passe`)} />
      )}
      {secret && (
        <Fenetre titre="Nouveau mot de passe provisoire" onFermer={() => setSecret(null)}>
          <SecretUnique identifiant={secret.identifiant} motDePasse={secret.mot_de_passe_provisoire} onTermine={() => setSecret(null)} />
        </Fenetre>
      )}

      {/* ─── Actions sur une fonction ─── */}
      {action?.type === 'attribuer' && p && (
        <FenetreFonction api={api} moi={moi} personne={{ id: p.id, nom, type: p.type, acces_fin: x ? x.date_fin : null }}
          onFermer={() => setAction(null)} onFait={() => fait({ message: 'Fonction attribuée.' })} />
      )}
      {action?.type === 'f-suspendre' && (
        <FenetreSaisie titre={`Suspendre la fonction — ${nom}`} onFermer={() => setAction(null)} onFait={fait} champs={[motif]}
          avertissement="Les droits IPM de la fonction sont retirés tant qu'elle est suspendue." libelleBouton="Suspendre"
          valider={(v) => api.post(`/equipe/fonctions/${action.f.id}/suspendre`, v)} />
      )}
      {action?.type === 'f-reactiver' && (
        <FenetreSaisie titre={`Réactiver la fonction — ${nom}`} onFermer={() => setAction(null)} onFait={fait} champs={[motif]} libelleBouton="Réactiver"
          valider={(v) => api.post(`/equipe/fonctions/${action.f.id}/reactiver`, v)} />
      )}
      {action?.type === 'f-prolonger' && (
        <FenetreSaisie titre={`Prolonger la fonction — ${nom}`} onFermer={() => setAction(null)} onFait={fait} libelleBouton="Prolonger"
          avertissement={x ? `La fonction ne peut pas dépasser la fin de l'accès (${formaterDate(x.date_fin)}) : renouvelez d'abord l'accès si besoin.` : null}
          initial={{ date_fin: action.f.date_fin || '' }}
          champs={[{ cle: 'date_fin', libelle: 'Nouvelle date de fin', requis: true, type: 'date', min: action.f.date_debut, max: x ? x.date_fin : undefined }, motif]}
          valider={(v) => api.post(`/equipe/fonctions/${action.f.id}/prolonger`, v)} />
      )}
      {action?.type === 'f-terminer' && (
        <FenetreSaisie titre={`Terminer la fonction — ${nom}`} onFermer={() => setAction(null)} onFait={fait} tonAvertissement="alerte"
          avertissement="Définitif : une fonction terminée ne se réactive pas. L'historique est conservé." champs={[motif]} libelleBouton="Terminer la fonction"
          valider={(v) => api.post(`/equipe/fonctions/${action.f.id}/terminer`, v)} />
      )}
    </Fenetre>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────
export default function IpmEquipePage() {
  const api = useApi();
  const { employe } = useAuth();
  const moi = employe?.id;
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [termines, setTermines] = useState(false);
  const [fenetre, setFenetre] = useState(null);
  const [message, setMessage] = useState(null);
  const charger = useCallback(() => {
    setErreur(null);
    api.get('/equipe').then(setData).catch((e) => setErreur(messageErreur(e)));
  }, [api]);
  useEffect(() => { charger(); }, [charger]);

  const droits = data?.droits || {};
  const dureeMax = data?.duree_max_mois || 12;
  const lectureSeule = !!data && !droits.administration && !droits.supervision;
  const lignes = data ? data.equipe.filter((r) => termines || statutDe(r) !== 'TERMINEE') : [];
  const nbTermines = data ? data.equipe.filter((r) => statutDe(r) === 'TERMINEE').length : 0;
  const ouvrir = (r) => setFenetre({ type: 'detail', ligne: r });
  const touche = (e, r) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrir(r); } };
  const statut = (r) => STATUT_EFFECTIF[statutDe(r)] || { libelle: statutDe(r), ton: 'neutre' };
  const pastilles = (r) => (r.alertes || []).map((a) => <Badge key={a.code} ton="alerte" title={a.message}>{ALERTE_COURTE[a.code] || a.message}</Badge>);
  const fonctionCellule = (r) => {
    const m = mentionsFonction(r);
    return (
      <>
        <div>{libelleFonction(r)}</div>
        {m.length > 0 && <div className="rh-cellule-secondaire">{m.join(' · ')}</div>}
      </>
    );
  };

  return (
    <Layout>
      <div className="rh-page rh-page-large">
        <EnTetePage fil={[{ libelle: 'IPM', vers: '/ipm' }, { libelle: 'Équipe IPM' }]} titre="Équipe IPM"
          sousTitre="Agents et Gestionnaires IPM, salariés SOGAS ou prestataires externes. La fonction IPM est nominative et datée ; elle ne change pas le poste RH."
          actions={droits.administration && (
            <>
              <Bouton Icone={UserCog} onClick={() => setFenetre({ type: 'fonction' })}>Attribuer une fonction à un salarié</Bouton>
              <Bouton variante="primaire" Icone={UserPlus} onClick={() => setFenetre({ type: 'prestataire' })}>Ajouter un prestataire</Bouton>
            </>
          )} />
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
        {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
        {!data && !erreur && <Squelette hauteur={200} />}
        {!data && erreur && <EtatVide Icone={ShieldAlert} titre="Équipe IPM inaccessible" texte="Le suivi des accès IPM relève du Gestionnaire IPM ou du service RH." />}
        {data && (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={termines} onChange={(e) => setTermines(e.target.checked)} /> Afficher les accès terminés{nbTermines ? ` (${nbTermines})` : ''}
              </label>
              {lectureSeule && <span className="rh-cellule-secondaire">{MSG_LECTURE_EXTERNE}</span>}
              {!lectureSeule && !droits.administration && (
                <span className="rh-cellule-secondaire">La création d'un accès et l'attribution d'une fonction à un salarié relèvent de la DRH ou du RH principal. La fonction d'un prestataire peut être attribuée par un Gestionnaire IPM salarié qui n'a pas créé son accès.</span>
              )}
            </div>
            {lignes.length === 0 ? (
              <EtatVide Icone={Users} titre="Aucun membre" texte="Aucun Agent ni Gestionnaire IPM pour l'instant." />
            ) : (
              <div className="rh-conteneur-tableau">
                <table className="rh-tableau rh-tableau-bureau">
                  <thead><tr>
                    <th>Personne</th><th>Fonction</th><th>Statut</th><th className="rh-col-optionnelle">Début</th><th>Fin</th><th>Échéance</th><th className="rh-col-optionnelle">Parrain</th><th aria-label="Ouvrir" />
                  </tr></thead>
                  <tbody>
                    {lignes.map((r) => (
                      <tr key={r.personne_id} className="rh-ligne" tabIndex={0} role="link" aria-label={`Ouvrir le détail de ${nomPersonne(r)}`}
                        onClick={() => ouvrir(r)} onKeyDown={(e) => touche(e, r)}>
                        <td>
                          <div className="rh-cellule-principale">{nomPersonne(r)}{memeId(r.personne_id, moi) ? <span className="rh-cellule-secondaire"> (vous)</span> : null}</div>
                          <div className="rh-cellule-secondaire">{LIBELLE_TYPE[r.type]} · {r.identifiant || NON_RENSEIGNE}{r.organisme ? ` · ${r.organisme}` : ''}</div>
                        </td>
                        <td>{fonctionCellule(r)}</td>
                        <td>
                          <Etat ton={statut(r).ton}>{statut(r).libelle}</Etat>
                          {(r.alertes || []).length > 0 && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>{pastilles(r)}</div>}
                        </td>
                        <td className="rh-col-optionnelle" style={{ whiteSpace: 'nowrap' }}>{formaterDate(debutDe(r)) || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formaterDate(finDe(r)) || 'Sans date de fin'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{echeance(r)}</td>
                        <td className="rh-col-optionnelle">{r.type === 'EXTERNE' ? (r.parrain_nom || NON_RENSEIGNE) : '—'}</td>
                        <td style={{ color: 'var(--sg-texte-4)' }}><ChevronRight size={16} aria-hidden="true" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <ul className="rh-cartes-liste" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {lignes.map((r) => (
                    <li key={r.personne_id} className="rh-ligne" tabIndex={0} role="link" aria-label={`Ouvrir le détail de ${nomPersonne(r)}`}
                      onClick={() => ouvrir(r)} onKeyDown={(e) => touche(e, r)}
                      style={{ display: 'flex', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--sg-bord)', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="rh-cellule-principale">{nomPersonne(r)}{memeId(r.personne_id, moi) ? <span className="rh-cellule-secondaire"> (vous)</span> : null}</div>
                        <div className="rh-cellule-secondaire">{LIBELLE_TYPE[r.type]} · {r.identifiant || NON_RENSEIGNE}</div>
                        <div className="rh-cellule-secondaire">{libelleFonction(r)}{mentionsFonction(r).length ? ` · ${mentionsFonction(r).join(' · ')}` : ''}</div>
                        <div className="rh-cellule-secondaire">
                          {formaterDate(debutDe(r)) || '—'} → {formaterDate(finDe(r)) || 'sans date de fin'} · échéance : {echeance(r)}
                        </div>
                        {r.type === 'EXTERNE' && <div className="rh-cellule-secondaire">Parrain : {r.parrain_nom || NON_RENSEIGNE}</div>}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
                          <Etat ton={statut(r).ton}>{statut(r).libelle}</Etat>
                          {pastilles(r)}
                        </div>
                      </div>
                      <ChevronRight size={16} color="var(--sg-texte-4)" aria-hidden="true" style={{ marginTop: 10, flexShrink: 0 }} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {fenetre?.type === 'detail' && (
          <DetailPersonne api={api} droits={droits}
            ligne={(data && data.equipe.find((r) => r.personne_id === fenetre.ligne.personne_id)) || fenetre.ligne} moi={moi} dureeMax={dureeMax}
            onFermer={() => setFenetre(null)} onChange={charger} />
        )}
        {fenetre?.type === 'prestataire' && (
          <FenetrePrestataire api={api} dureeMax={dureeMax} onFermer={() => setFenetre(null)}
            onFait={() => { setFenetre(null); setMessage('Accès du prestataire créé.'); charger(); }} />
        )}
        {fenetre?.type === 'fonction' && (
          <FenetreFonction api={api} moi={moi} onFermer={() => setFenetre(null)}
            onFait={() => { setFenetre(null); setMessage('Fonction attribuée. Le poste RH du salarié est inchangé.'); charger(); }} />
        )}
      </div>
    </Layout>
  );
}
